import React, { FC, useState } from "react";
import styled from "styled-components";

import { Button } from "../../../../../../../components/atoms";
import { ModalView } from "../../../../../../../components/organisms/Modal/Modal";
import { InputWithWildCard } from "../../../../../../../components/molecules/InputWithWildCard/InputWithWildCard";
import { Icon } from "../../../../../../../components/atoms";

import validatePan from "../../../../../../../utils/validateCardPan";
import { BankCard } from "../../../../../../../models/BankCard.model";
import { SetAlert } from "../../../../../../../store/actions/alert/AlertActions";
import { useDispatch } from "react-redux";

import { FormError } from "../../../../../../otcTether/types/FormError";

import BankCardDetails from "./BankCardDetails";
import {
  ADD_CARDS,
  VERIFY_CARDS,
} from "./../../../../../../../api-calls/CardApi";
import { useTranslation } from "react-i18next";
import i18n from "src/i18nextConf";

type OwnProps = {
  showDialog: boolean;
  onCloseDialog: () => void;
  onConfirmedCard: (confirmedCard: BankCard | undefined) => void;
};

const AddCardWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row wrap;
  padding: 28px 24px;

  .add-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 40px;
    width: 100%;

    #title {
      font-size: 16px;
      font-weight: 800;
      color: ${(theme) => theme.theme.Light1};
    }
  }

  .add-card-input {
    width: 100%;
    margin-top: 4px;

    #add-input-title {
      width: 100%;
      margin-bottom: 16px;
      display: block;
      font-weight: 400;
      font-size: 14px;
      color: ${(theme) => theme.theme.Light1};
    }
  }

  .bank-card-details-holder {
    margin-top: 20px;
  }

  .confirm-add-cardwrapper {
    margin-top: 20px;
    width: 100%;
  }
`;

const HelpBlock = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  .help-text {
    margin-inline-start: 16px;
    color: ${(theme: any) => theme.theme.etRed};
    span {
      font-size: 14px;
      &:not(:first-child) {
        margin-inline-start: 5px;
      }
    }
  }
`;

const AddCardDialog: FC<OwnProps> = ({
  showDialog = false,
  onCloseDialog,
  onConfirmedCard,
}) => {
  const dispatch = useDispatch();

  const [cardNumber, setCardNumber] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [cardData, setCardData] = useState<BankCard>();

  const [formError, setFormError] = useState<FormError>({
    hasError: false,
    message: "",
  });

  const {t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';

  const addCard = async () => {
    setIsLoading(true);
    let valid = validatePan(cardNumber);
    if (!valid) {
      setFormError({
        hasError: true,
        message: t("deposit.addCardDialog.helpWrongNum"),
      });

      setIsLoading(false);
      return;
    }
    await ADD_CARDS(cardNumber).then(
      (res) => {
        setIsLoading(false);
        setCardData(res.data.data);
        setFormError({
          hasError: false,
          message: "",
        });
      },
      (error) => {
        setIsLoading(false);
        let msg = t("deposit.addCardDialog.errAdd");
        if (error?.data?.message) {
          msg = error?.data?.message;
        }
        setFormError({
          hasError: true,
          message: msg,
        });
      }
    );
  };

  const confirmCard = async () => {
    setIsLoading(true);
    VERIFY_CARDS(cardData?.id || 0).then(
      (res) => {
        setIsLoading(false);
        setCardData(res.data.data);
        //Get the Parent Confirmed Card
        onConfirmedCard(cardData);
        dispatch(
          SetAlert({
            btnText: t("deposit.addCardDialog.btn"),
            message: t("deposit.addCardDialog.btnMessage"),
            title: t("deposit.addCardDialog.btnTitle"),
            type: "success",
            show: true,
          })
        );

        handleCloseDialog();
      },
      (error) => {
        setIsLoading(false);
        let msg = t("deposit.addCardDialog.errAdd");
        if (error?.data?.message) {
          msg = error?.data?.message;
        }
        dispatch(
          SetAlert({
            btnText: t("deposit.addCardDialog.btn"),
            message: msg,
            title: t("deposit.addCardDialog.btnTitle"),
            type: "warning",
            show: true,
          })
        );
      }
    );
  };

  const handleConfirmClicked = () => {
    setIsLoading(true);
    if (cardData?.id) {
      confirmCard();
    } else {
      addCard();
    }
  };

  const handleCardPan = (value: any) => {
    setCardNumber(value.value);
  };

  const handleCloseDialog = () => {
    setCardNumber("");

    setCardData(undefined);

    setFormError({
      hasError: false,
      message: "",
    });
    // Call The Parent To Close The Dialog
    onCloseDialog();
  };

  return (
    <ModalView
      color="etDark8"
      width={"383px"}
      show={showDialog}
      handleClose={onCloseDialog}
    >
      <AddCardWrapper>
        <div className="add-card-header">
          <span id="title">{t("deposit.addCardDialog.title")}</span>
          <Button
            id="223"
            primary
            onClick={handleCloseDialog}
            width="66px"
            height="40px"
            color="white"
            text={t("deposit.addCardDialog.cnclBtn")}
            backgroundColor="bgButton"
          ></Button>
        </div>

        {!cardData && (
          <div className="add-card-input">
            <span id="add-input-title" style={{ textAlign: isRtl ? 'right' : 'left' }}>
            {t("deposit.addCardDialog.desc")}
            </span>
            <InputWithWildCard
              type={"tel"}
              wild=""
              placeholder=""
              name="wallet"
              defaultValue={cardNumber}
              handleOnChange={(data) => handleCardPan(data)}
              direction={"ltr"}
              maskType={"CARD"}
            />

            {formError.hasError && (
              <HelpBlock>
                <Icon name="helpInfo" width="15px" height="15px" />
                <div className="help-text">
                  <span>{formError.message}</span>
                </div>
              </HelpBlock>
            )}
          </div>
        )}

        {cardData && (
          <div className="bank-card-details-holder">
            <BankCardDetails bankCardData={cardData} />
          </div>
        )}

        <div className="confirm-add-cardwrapper">
          <Button
            id="224"
            primary
            isLoading={isLoading}
            onClick={handleConfirmClicked}
            width="100%"
            height="48px"
            color="white"
            text={t("deposit.addCardDialog.confirmBtn")}
            backgroundColor="bgPrimary"
          ></Button>
        </div>
      </AddCardWrapper>
    </ModalView>
  );
};

export default AddCardDialog;
